import React, { useState } from 'react';
import { MapPin, Clock, Phone, Mail, MessageCircle, Calendar, ChevronRight } from 'lucide-react';

const ContactUs = () => {
  const [activeTab, setActiveTab] = useState('message');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Haircut',
    'Color',
    'Highlights',
    'Balayage',
    'Treatment',
    'Styling',
    'Threading',
    'Other'
  ];

  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[30vh] sm:h-[35vh] lg:h-[40vh] bg-[#F9F0F3] overflow-hidden flex items-center pt-16 lg:pt-20">
        <div className="absolute inset-0">
          <div className="absolute right-0 top-0 w-full sm:w-1/2 h-full">
            <img
              src="/api/placeholder/1000/1000"
              alt="Salon interior"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#F9F0F3]/50 to-[#F9F0F3]" />
          </div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative">
          <div className="max-w-xl">
            <span className="text-xs sm:text-sm uppercase tracking-widest text-[#A47C89] mb-2 sm:mb-4 block">
              Get in Touch
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-4 sm:mb-6">
              Let's Create
              <span className="block font-playfair italic text-[#A47C89]">Your Story</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Whether you're ready to book an appointment or have questions about our services,
              we're here to help you begin your transformation journey.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="bg-white py-8 sm:py-12 border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <a
              href="https://square.site/book/5CPJFK4WYVQF3/cut-n-curls-salon-manhasset-ny"
              className="group bg-[#F9F0F3] p-6 sm:p-8 rounded-lg flex items-center justify-between transition-all hover:bg-[#A47C89] hover:text-white"
            >
              <div>
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mb-3 sm:mb-4 text-[#A47C89] group-hover:text-white" />
                <h3 className="text-lg sm:text-xl font-medium mb-1 sm:mb-2">Book Online</h3>
                <p className="text-xs sm:text-sm opacity-80">Schedule your next appointment</p>
              </div>
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all" />
            </a>
            <button
              onClick={() => setActiveTab('message')}
              className="group bg-[#F9F0F3] p-6 sm:p-8 rounded-lg flex items-center justify-between transition-all hover:bg-[#A47C89] hover:text-white"
            >
              <div>
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 mb-3 sm:mb-4 text-[#A47C89] group-hover:text-white" />
                <h3 className="text-lg sm:text-xl font-medium mb-1 sm:mb-2">Send Message</h3>
                <p className="text-xs sm:text-sm opacity-80">Get in touch with our team</p>
              </div>
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all" />
            </button>
            <a
              href="tel:5165446582"
              className="group bg-[#F9F0F3] p-6 sm:p-8 rounded-lg flex items-center justify-between transition-all hover:bg-[#A47C89] hover:text-white"
            >
              <div>
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 mb-3 sm:mb-4 text-[#A47C89] group-hover:text-white" />
                <h3 className="text-lg sm:text-xl font-medium mb-1 sm:mb-2">Call Us</h3>
                <p className="text-xs sm:text-sm opacity-80">(516) 544-6582</p>
              </div>
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all" />
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left Column - Contact Info */}
            <div>
              <div className="lg:sticky lg:top-8">
                <span className="text-xs sm:text-sm uppercase tracking-widest text-[#A47C89] mb-3 sm:mb-4 block">
                  Visit Our Salon
                </span>
                <h2 className="text-3xl sm:text-4xl font-playfair mb-8 sm:mb-12">Let's Meet in Person</h2>

                <div className="space-y-6 sm:space-y-8">
                  <div className="border-l-2 border-[#F9F0F3] pl-6 sm:pl-8 py-3 sm:py-4 group hover:border-[#A47C89] transition-colors">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#A47C89] mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl mb-2">Location</h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      593 Plandome Rd<br />
                      Manhasset, NY 11030
                    </p>
                  </div>

                  <div className="border-l-2 border-[#F9F0F3] pl-6 sm:pl-8 py-3 sm:py-4 group hover:border-[#A47C89] transition-colors">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-[#A47C89] mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl mb-2">Hours</h3>
                    <div className="space-y-2 text-gray-600 text-sm sm:text-base">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                        <span>Monday</span>
                        <span className="text-right">10:00 AM - 5:00 PM</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                        <span>Tuesday | Wednesday | Friday | Saturday</span>
                        <span className="text-right">9:00 AM - 6:00 PM</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                        <span>Thursday</span>
                        <span className="text-right">9:00 AM - 7:00 PM</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                        <span>Sunday</span>
                        <span className="text-right">Closed</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-2 border-[#F9F0F3] pl-6 sm:pl-8 py-3 sm:py-4 group hover:border-[#A47C89] transition-colors">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-[#A47C89] mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl mb-2">Email</h3>
                    <a href="mailto:cutncurlssalon@gmail.com" className="text-gray-600 text-sm sm:text-base hover:text-[#A47C89] transition-colors break-all">
                      cutncurlssalon@gmail.com
                    </a>
                  </div>
                </div>

                {/* Map */}
                <div className="mt-8 sm:mt-12 aspect-[4/3] relative rounded-lg overflow-hidden">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3021.1605432650452!2d-73.70459492427241!3d40.80030556842946!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c2891bf718c6cb%3A0x8ff5a39c3c2ad19b!2sCut%20N%20Curls%20Salon!5e0!3m2!1sen!2sus!4v1705961231733!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    loading="lazy"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-none"
                    title="Google Maps location of Cut N Curls"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div>
              <div className="bg-[#F9F0F3] p-6 sm:p-8 lg:p-12 rounded-lg">
                <span className="text-xs sm:text-sm uppercase tracking-widest text-[#A47C89] mb-3 sm:mb-4 block">
                  Send Us a Message
                </span>
                <h2 className="text-3xl sm:text-4xl font-playfair mb-6 sm:mb-8">How Can We Help?</h2>
                
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4 sm:space-y-6"
                >
                  <div>
                    <label className="block text-gray-700 mb-2 text-xs sm:text-sm" htmlFor="name">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-0 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A47C89]"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 mb-2 text-xs sm:text-sm" htmlFor="email">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-0 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A47C89]"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 mb-2 text-xs sm:text-sm" htmlFor="phone">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-0 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A47C89]"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-2 text-xs sm:text-sm" htmlFor="service">
                      Service Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-0 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A47C89]"
                      required
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2 text-xs sm:text-sm" htmlFor="message">
                      Your Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 border-0 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A47C89]"
                      required
                    ></textarea>
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm">
                      Thank you! Your message has been sent successfully. We'll get back to you soon.
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                      Sorry, there was an error sending your message. Please try again or call us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-colors text-sm sm:text-base ${
                      isSubmitting 
                        ? 'bg-gray-400 text-white cursor-not-allowed' 
                        : 'bg-black text-white hover:bg-gray-900'
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactUs;